// User preferences. Lives in localStorage only (no server-side sync yet —
// retention / notifications / email summary would need backend work before
// those preferences actually do anything).
//
// The store exposes a subscribe() so components can re-render when settings
// change, and `applyAppearance()` flips CSS variables / data-theme on <html>
// so the color scheme, accent, and font size update instantly.

export type ColorScheme = "light" | "dark" | "system";
export type AccentColor = "amber" | "sage" | "rose" | "blue" | "teal";
export type FontSize = "small" | "medium" | "large";
export type SortOrder = "newest" | "oldest";
export type Retention = "never" | "1y" | "3y";
export type ResponseStyle = "concise" | "detailed";

export type AiProviderMode = "auto" | "local" | "cloud";

export type AiTask =
  | "ocr_image"
  | "ocr_frame"
  | "text_extraction_meta"
  | "case_summary"
  | "record_summary"
  | "suggested_action"
  | "draft_letter"
  | "draft_complaint"
  | "interrogate_simple"
  | "interrogate_complex";

export interface Settings {
  appearance: {
    colorScheme: ColorScheme;
    accent: AccentColor;
    fontSize: FontSize;
  };
  records: {
    sortOrder: SortOrder;
    groupByMonth: boolean;
    retention: Retention;
  };
  interrogate: {
    model: string; // future-proofed; today only one value
    responseStyle: ResponseStyle;
    language: string; // ISO 639-1 code or locale (e.g. "en", "en-US", "fr")
  };
  notifications: {
    emailOnForward: boolean;
    weeklySummary: boolean;
  };
  account: {
    displayName: string;
  };
  ai: {
    provider: AiProviderMode;
    privacy: boolean;
    overrides: Partial<Record<AiTask, string>>;
  };
}

export const DEFAULT_SETTINGS: Settings = {
  appearance: {
    colorScheme: "dark",
    accent: "amber",
    fontSize: "medium",
  },
  records: {
    sortOrder: "newest",
    groupByMonth: true,
    retention: "never",
  },
  interrogate: {
    model: "claude-sonnet-4-6",
    responseStyle: "concise",
    language: "en",
  },
  notifications: {
    emailOnForward: false,
    weeklySummary: false,
  },
  account: {
    displayName: "",
  },
  ai: {
    provider: "auto",
    privacy: false,
    overrides: {},
  },
};

const STORAGE_KEY = "casethread:settings";

function mergeDeep<T>(base: T, patch: unknown): T {
  if (!patch || typeof patch !== "object") return base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    const curr = out[k];
    if (
      curr &&
      typeof curr === "object" &&
      !Array.isArray(curr) &&
      v &&
      typeof v === "object" &&
      !Array.isArray(v)
    ) {
      out[k] = mergeDeep(curr, v);
    } else {
      out[k] = v;
    }
  }
  return out as T;
}

export function loadSettings(): Settings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return mergeDeep(DEFAULT_SETTINGS, JSON.parse(raw));
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(s: Settings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Private mode / quota exceeded — preferences simply don't persist.
  }
}

// Tiny pub-sub so the whole UI can re-render on settings change without
// threading a context through every component.
type Listener = (s: Settings) => void;
const listeners = new Set<Listener>();

let current: Settings = loadSettings();

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings>): Settings {
  current = mergeDeep(current, patch);
  saveSettings(current);
  applyAppearance(current);
  for (const l of listeners) l(current);
  return current;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// -----------------------------------------------------------------------------
// Appearance side-effects.
//
// Theme + accent + brightness are owned by lib/theme.ts now; the theme
// class on <html> drives all colour variables. The only appearance
// responsibility left here is font size, which is still a Settings
// slider and is independent of the colour theme.
// -----------------------------------------------------------------------------

const FONT_SIZE_MAP: Record<FontSize, string> = {
  small: "13px",
  medium: "14px",
  large: "16px",
};

export function applyAppearance(s: Settings = current): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--font-size-base",
    FONT_SIZE_MAP[s.appearance.fontSize],
  );
}
